import { parseCognitoRoles } from '@metabolic/shared';
import {
  startAuthentication,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { publicFetch } from '../lib/api-client';
import { logger } from '../lib/logger';

/**
 * User roles defined in Cognito User Pool Groups
 */
export type UserRole = 'admin' | 'coach' | 'user';

export interface AuthUser {
  username: string;
  email?: string;
  roles: UserRole[];
}

export interface SignInResult {
  isSignedIn: boolean;
  error?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: () => Promise<SignInResult>;
  signOut: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * AuthProvider Component
 *
 * Manages authentication state and provides auth methods to the app.
 * Integrates with AWS Cognito for passkey-based authentication.
 *
 * Authentication Flow:
 * - Uses USER_AUTH flow for passkey (WebAuthn) authentication
 * - No passwords required for sign-in (passwordless)
 * - Emergency recovery passwords exist but are not user-facing
 *
 * RBAC Implementation:
 * - Reads Cognito User Pool Groups from JWT token claims
 * - Provides hasRole() and hasAnyRole() helpers for UI rendering
 * - Backend enforcement happens via Lambda authorizers
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is already authenticated on mount
  useEffect(() => {
    checkAuthState();
  }, []);

  async function checkAuthState() {
    try {
      // Get session from Amplify
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      // Extract Cognito groups from JWT token and parse to UserRole[]
      // parseCognitoRoles handles the type checking internally
      const roles = parseCognitoRoles(
        session.tokens?.accessToken.payload['cognito:groups'] as
          string | string[] | undefined
      );

      setUser({
        username: currentUser.username,
        email: (session.tokens?.idToken?.payload.email as string) || undefined,
        roles,
      });
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSignIn(): Promise<SignInResult> {
    try {
      logger.debug('[Auth] Starting usernameless sign-in');

      // Step 1: Get WebAuthn challenge from backend (no email needed!)
      logger.debug('[Auth] Step 1: Requesting WebAuthn challenge');
      const { sessionId, authenticationOptions } = await publicFetch<{
        sessionId: string;
        authenticationOptions: PublicKeyCredentialRequestOptionsJSON;
      }>('/v1/auth/initiate-signin', {
        method: 'POST',
        body: JSON.stringify({}), // Empty body - no email required!
      });
      logger.debug('[Auth] Received authentication options', {
        sessionId,
        rpId: authenticationOptions.rpId,
        credentialCount: authenticationOptions.allowCredentials?.length,
        timeout: authenticationOptions.timeout,
      });

      // Step 2: Perform WebAuthn authentication
      logger.debug('[Auth] Step 2: Starting WebAuthn authentication');
      logger.debug('[DEBUG] Calling startAuthentication', {
        timestamp: Date.now(),
        options: authenticationOptions,
      });
      const authenticationResponse = await startAuthentication({
        optionsJSON: authenticationOptions,
      });
      logger.debug('[DEBUG] startAuthentication succeeded', {
        timestamp: Date.now(),
        response: authenticationResponse,
      });
      logger.debug('[Auth] WebAuthn authentication successful');

      // Step 3: Verify WebAuthn and get passkey proof (backend identifies user by credential ID)
      logger.debug('[Auth] Step 3: Verifying WebAuthn response');
      const { userId, cognitoUsername, passkeyProof } = await publicFetch<{
        userId: string;
        cognitoUsername: string;
        passkeyProof: string;
      }>('/v1/auth/verify-webauthn', {
        method: 'POST',
        body: JSON.stringify({
          sessionId, // Include session ID to retrieve the correct challenge
          authenticationResponse, // No email - backend looks up user by credential ID!
        }),
      });
      logger.debug('[Auth] WebAuthn verification successful', {
        userId,
        cognitoUsername,
      });

      // Step 4: Start Amplify sign-in with passkey proof
      // IMPORTANT: Must use cognitoUsername (NOT userId/sub) for Cognito authentication
      // Must include passkeyProof in clientMetadata on first call
      // The define-auth-challenge trigger requires it in session 0
      logger.debug('[Auth] Step 4: Starting Amplify sign-in', {
        cognitoUsername,
        userId,
      });
      const { signIn } = await import('aws-amplify/auth');
      await signIn({
        username: cognitoUsername, // Use Cognito username (UID), NOT userId (sub)!
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
          clientMetadata: {
            passkeyProof,
            userId, // Pass userId (sub) in metadata for proof validation
          },
        },
      });
      logger.debug('[Auth] Amplify sign-in initiated');

      // Step 5: Complete sign-in with challenge response
      // This triggers verify-auth-challenge which validates the proof
      logger.debug('[Auth] Step 5: Confirming sign-in');
      const { confirmSignIn } = await import('aws-amplify/auth');
      await confirmSignIn({
        challengeResponse: passkeyProof,
        options: {
          clientMetadata: {
            passkeyProof,
            userId, // Required by verify-auth-challenge trigger
          },
        },
      });
      logger.debug('[Auth] Sign-in confirmed');

      // Step 6: Refresh auth state from Amplify session
      logger.debug('[Auth] Step 6: Refreshing auth state');
      await checkAuthState();
      logger.debug('[Auth] Sign-in complete');

      return { isSignedIn: true };
    } catch (error) {
      // Log full error details including cause chain for NotAllowedError
      const errorDetails: Record<string, unknown> = {
        error,
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
      };

      // Capture .cause property for ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY errors
      if (error && typeof error === 'object' && 'cause' in error) {
        errorDetails.cause = error.cause;
      }

      logger.error('[Auth] Sign-in failed', errorDetails);

      const message =
        error instanceof Error
          ? error.message
          : 'Failed to sign in with passkey';
      return { isSignedIn: false, error: message };
    }
  }

  async function handleSignOut() {
    // Clear Amplify session
    try {
      await signOut();
    } catch {
      // Ignore errors if no session exists
    }

    setUser(null);
  }

  function hasRole(role: UserRole): boolean {
    return user?.roles.includes(role) ?? false;
  }

  function hasAnyRole(roles: UserRole[]): boolean {
    return roles.some((role) => hasRole(role));
  }

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    signIn: handleSignIn,
    signOut: handleSignOut,
    hasRole,
    hasAnyRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth Hook
 *
 * Access authentication state and methods from any component.
 *
 * @example
 * ```tsx
 * const { user, signIn, signOut, hasRole } = useAuth();
 *
 * if (hasRole('admin')) {
 *   return <AdminPanel />;
 * }
 * ```
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
